/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import classNames from 'classnames';
import * as React from 'react';
import { Tooltip, TooltipProps } from '../tooltip/tooltip';

export interface SliderProps extends React.HTMLAttributes<HTMLDivElement> {
    labelLeft?: SliderLabelType;
    labelRight?: SliderLabelType;
    labelsOnTop?: boolean;
    tooltip?: boolean | string | TooltipProps;
    tooltipUnit?: string;
    isTooltipInPercent?: boolean;
    input?: React.InputHTMLAttributes<HTMLInputElement>;
}

/**
 * @name        a-slider
 * @type        atom
 *
 * @description
 * representation of a slider
 */

function getLabelElement(
    labelLeft: SliderLabelType,
    inputProps: React.InputHTMLAttributes<HTMLInputElement>,
) {
    let left = labelLeft;
    if (typeof left === 'string') {
        left = <label htmlFor={inputProps.id}>{labelLeft}</label>;
    } else if (typeof left === 'object') {
        left = <label {...left} />;
    }
    return left;
}

function calculatePercentageValue(slider?: HTMLInputElement): number {
    const min: number = slider?.min ? Number(slider.min) : 0;
    const max: number = slider?.max ? Number(slider.max) : 100;
    return Number(((slider?.valueAsNumber - min) * 100) / (max - min));
}

function calculateTooltipOffset(tooltip?: HTMLElement, percentage = 0): number {
    const tooltipOffsetRem = tooltip?.offsetWidth / 2 / 16;
    const thumbOffsetFactor = percentage / 100 - 0.5;

    // thumb has 1.5rem
    return tooltipOffsetRem + thumbOffsetFactor * 1.5;
}

function getValue(
    isTooltipInPercent: boolean,
    percentage: number,
    inputRef: React.MutableRefObject<HTMLInputElement>,
) {
    return isTooltipInPercent
        ? Math.round(percentage)
        : inputRef.current?.value || 0;
}

function getTooltipProps(
    tooltip: string | boolean | TooltipProps,
    val: React.Key,
    tooltipUnit: string,
): TooltipProps {
    return typeof tooltip === 'object'
        ? { ...tooltip }
        : {
              content: `${
                  typeof tooltip === 'string' ? tooltip : val
              } ${tooltipUnit}`,
          };
}

export const Slider = React.forwardRef<HTMLDivElement, SliderProps>(
    (
        {
            input = {},
            labelLeft,
            labelRight,
            labelsOnTop,
            tooltip,
            tooltipUnit = '',
            isTooltipInPercent,
            ...rest
        },
        ref,
    ) => {
        const [tooltipVisible, setTooltipVisible] = React.useState(false);

        const divClass = classNames('a-slider', {
            'a-slider--labels-on-top': labelsOnTop,
        });
        const inputRef = React.useRef<HTMLInputElement>();
        const tooltipRef = React.useRef<HTMLElement>();

        React.useEffect(() => {
            if (inputRef.current) {
                const relVal = calculatePercentageValue(inputRef.current);
                inputRef.current.style.setProperty('--progress', relVal + '%');
            }
        });

        function updateTooltip(e: React.MouseEvent<HTMLInputElement>) {
            const type = e.type;
            if (type === 'mouseenter') {
                setTooltipVisible(true);
                inputRef.current?.style.setProperty('--color', '#00629a');
                input.onMouseOver?.(e);
            } else if (type === 'mouseleave') {
                setTooltipVisible(false);
                inputRef.current?.style.setProperty('--color', '#007bc0');
                input.onMouseLeave?.(e);
            } else if (type === 'mousedown') {
                inputRef.current?.style.setProperty('--color', '#004975');
                input.onMouseDown?.(e);
            } else if (type === 'mouseup') {
                inputRef.current?.style.setProperty('--color', '#00629a');
                input.onMouseUp?.(e);
            }
        }

        const left = getLabelElement(labelLeft, input);
        const right = getLabelElement(labelRight, input);

        const percentage = calculatePercentageValue(inputRef.current) || 0;

        const offsetRem =
            calculateTooltipOffset(tooltipRef.current, percentage) || 0;

        const val = getValue(isTooltipInPercent, percentage, inputRef);

        const tooltipProps: TooltipProps = getTooltipProps(
            tooltip,
            val,
            tooltipUnit,
        );

        const inputElement = (
            <input
                {...input}
                onMouseEnter={updateTooltip}
                onMouseLeave={updateTooltip}
                onMouseDown={updateTooltip}
                onMouseUp={updateTooltip}
                type="range"
                ref={inputRef}
            />
        );

        return (
            <div className={divClass} ref={ref} {...rest}>
                {left}
                <div>
                    {tooltip && (
                        <>
                            <Tooltip
                                style={{
                                    ...(tooltipProps.style || {}),
                                    visibility: tooltipVisible
                                        ? 'visible'
                                        : 'hidden',
                                    left: `calc(${percentage}% - (${offsetRem}rem))`,
                                }}
                                ref={r => {
                                    tooltipRef.current = r;
                                }}
                                {...tooltipProps}
                            />
                            {inputElement}
                        </>
                    )}
                    {!tooltip && inputElement}
                </div>
                {right}
            </div>
        );
    },
);
Slider.displayName = 'Slider';

export type SliderLabelType =
    | string
    | React.DetailedHTMLProps<
          React.LabelHTMLAttributes<HTMLLabelElement>,
          HTMLLabelElement
      >
    | React.ReactElement<HTMLLabelElement>;
